import React, { useState, useEffect, useContext, useCallback, useRef } from 'react'
import { useHistory } from 'react-router-dom'
import { useIntl } from 'react-intl'
import { Alert, App, Button, Form, Modal, Progress, Result, Spin, Typography } from 'antd'
import { LoadingOutlined } from '@ant-design/icons'
import { captureException } from '@sentry/react'
import { addMinutes, isAfter } from 'date-fns'
import * as Sentry from '@sentry/react'
import chunk from 'lodash/chunk'
import isBoolean from 'lodash/isBoolean'
import isNumber from 'lodash/isNumber'
import { useManualQuery } from 'graphql-hooks'

import Api from '@vacationtracker/shared/services/api'
import { selectAuthUserSlice } from '../../../store/auth-user-slice'
import { MicrosoftAuth } from '../../../services/auth/microsoft/microsoft'
import { GoogleAuth } from '../../../services/auth/google/google'
import { SlackAuth } from '../../../services/auth/slack/slack'
import { getName, getEmail, vtUserId } from '@vacationtracker/shared/functions/microsoft-common/microsoft-data-mappings'
import * as logger from '../../../services/logger'

import { getAllUserListShortPaginated, getGoogleScopes, getSlackDetails, slackIds } from '../../../graphql/custom-queries'
import { notificationStore } from '../../../context/notificationsContext/store'
import { useAppSelector } from '../../../store/hooks'
import { selectAuthCompanySlice } from '../../../store/auth-company-slice'
import { selectFeatureFlagsSlice } from '../../../store/feature-flags-slice'
import { track } from '../../../services/analytics/analytics'
import { useShouldEnableFeatures } from '../../../store/use-should-enable-features'
import { useGoogleAutoImportValidation } from '../../../hooks/use-google-auto-import-validation'

import { clearWizardState } from '../../../util/onboarding-wizard-storage'
import IntlMessages from '../../../util/IntlMessages'
import { PageHeader } from '../../../design-system/PageHeader'
import { PiUsers } from 'react-icons/pi'
import ImportUsersTable from './components/import-users-table'
import ImportFooter from './components/import-footer'
import ImportProgress from './components/import-progress'
import ImportAllToggle from './components/import-all-toggle'
import UserSearch from './components/user-search'
import BulkActionsToolbar from './components/bulk-actions-toolbar'
import ConfirmModal from './components/confirm-modal'
import AutoSyncNotice from './components/auto-sync-notice'
import { usePlatformUsers } from './hooks/use-platform-users'
import { useCsvImport } from './hooks/use-csv-import'
import { validateImportUsersRow } from './hooks/validate-csv-row'
import { ReactSpreadsheetImport } from 'react-spreadsheet-import'

import { IGetUserListShort } from '../../../types/teams'
import { IGetAllUserListShortPaginatedData } from '../../../types/users'
import { IMicrosoftUser } from '../../../types/microsoft'
import { IAutocompleteOption, IAutocompleteOptionValue, IImportData, IImportTableRow, IImportUser, IImportUsersEventBody, IUserToImportData } from './types'
import { Platform } from '@vacationtracker/shared/types/core-event'
import { ISlackUser } from '@vacationtracker/shared/types/repository/slack-api-repository'
import { SLACK_REQUIRED_BOT_SCOPES, SLACK_REQUIRED_USER_SCOPES } from '@vacationtracker/shared/data/slack'
import { ISlackIdUser, ISlackIds, IGetGoogleScopes, IGetSlackDetails, ISlackDetails } from '../../../types/custom-queries'
import { IUpcomingInvoiceInformation, zeroUpcomingInvoiceInformation } from '@vacationtracker/shared/types/stripe'
import { AUTOMATIC_USERS_IMPORT_LIMIT, GOOGLE_READ_USER_PERMISSION } from '../../../constants'
import { IGoogleScopes } from '../../../types/google'
import { FeatureFlagEnum } from '@vacationtracker/shared/types/feature-flags'
import { wait } from '@vacationtracker/shared/functions/wait'
import { SubscriptionPlanEnum } from '@vacationtracker/shared/types/company'
import { LocaleEnum } from '@vacationtracker/shared/types/i18n'
import { NotFound } from '@vacationtracker/shared/errors/request'

if (!process.env.REACT_APP_MICROSOFT_CLIENT_ID) {
  throw new Error('Client IDs are required')
}

const { Paragraph, Text } = Typography
const supportLink = 'https://vacationtracker.io/knowledge-base/google-workspace-editing-permissions/'
const IMPORT_BATCH_SIZE = 250

const msAuth = new MicrosoftAuth(process.env.REACT_APP_MICROSOFT_CLIENT_ID)
const slackAuth = new SlackAuth(process.env.REACT_APP_SLACK_CLIENT_ID)
const googleAuth = new GoogleAuth(process.env.REACT_APP_GOOGLE_CLIENT_ID)

const ImportUsersPage = (): React.ReactElement => {
  const { notification, message } = App.useApp()
  const [form] = Form.useForm()
  const { authCompany } = useAppSelector(selectAuthCompanySlice)
  const { formatMessage } = useIntl()
  const history = useHistory()
  const { eventsNotifications, setEventsNotifications } = useContext(notificationStore)
  const { featureFlags } = useAppSelector(selectFeatureFlagsSlice)
  const shouldEnableGoogleAdminPermissions = useShouldEnableFeatures(SubscriptionPlanEnum.core, FeatureFlagEnum.googleAdminPermissions, true)

  if (!authCompany?.platform) {
    throw new Error('Company is required!')
  }

  const platform = authCompany.platform

  const { actionNotifications, setActionNotifications } = useContext(notificationStore)
  const { departments, locations, leaveTypes, defaultDepartmentId, defaultLocationId, companyShouldProrate, companyProratedLeaveTypes } = usePlatformUsers()
  const [activeUsers, setActiveUsers] = useState<IGetUserListShort[]>([])
  const [isMsAdminConsentRequired, setMsAdminConsentRequired] = useState(false)
  const [adminConsentUrl, setAdminConsentUrl] = useState<null | string>(null)
  const [totalNumberOfUsers, setTotalNumberOfUsers] = useState(0)
  const [showAdminConsentWarning, setShowAdminConsentWarning] = useState(false)
  const [selectedUsers, setSelectedUsers] = useState<IAutocompleteOptionValue[]>([])
  const [showMsLoginRequiredModal, setShowMsLoginRequiredModal] = useState(false)
  const [showMsLoginAdditionalScopesRequiredModal, setShowMsLoginAdditionalScopesRequiredModal] = useState(false)
  const [showSlackLoginRequiredModal, setShowSlackLoginRequiredModal] = useState(false)
  const [showGoogleLoginRequiredModal, setShowGoogleLoginRequiredModal] = useState(false)
  const [showRequestGoogleAdminPermissionsModal, setShowRequestGoogleAdminPermissionsModal] = useState(false)
  const [showEnableGoogleApiModal, setShowEnableGoogleApiModal] = useState(false)
  const [showGoogleInsufficientPermissionsModal, setShowGoogleInsufficientPermissionsModal] = useState(false)
  const [notImportedUsers, setNotImportedUsers] = useState<IUserToImportData[]>([])
  const [hasLoadedNotImportedUsers, setHasLoadedNotImportedUsers] = useState(false)
  const [correlationId, setCorrelationId] = useState('')
  const [correlationExist, setCorrelationExist] = useState(false)
  const [importAllUsers, setImportAllUsers] = useState(false)
  const [importUsersAutomatically, setImportUsersAutomatically] = useState(Boolean(authCompany?.importUsersAutomatically))
  const [msTenantId, setMsTenantId] = useState('')
  const [slackUserDetails, setSlackUserDetails] = useState<ISlackDetails>()
  const { authUser } = useAppSelector(selectAuthUserSlice)
  const [googleScopes, setGoogleScopes] = useState<IGoogleScopes>()
  const [totalUsersAfterImport, setTotalUsersAfterImport] = useState(0)
  const [upcomingInvoiceInfo, setUpcomingInvoiceInfo] = useState<IUpcomingInvoiceInformation | null>(null)
  const [isLoadingUpcomingInvoiceInfo, setIsLoadingUpcomingInvoiceInfo] = useState(true)
  const [shouldEnableImportAll, setShouldEnableImportAll] = useState(false)
  const [noOfUsersToImport, setNoOfUsersToImport] = useState(0)
  const [tableRows, setTableRows] = useState<IImportTableRow[]>([])
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([])
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [isFetchingAllUsers, setIsFetchingAllUsers] = useState(false)
  const [importCompleted, setImportCompleted] = useState(false)
  const [importedCount, setImportedCount] = useState(0)
  const [importProgress, setImportProgress] = useState<{
    completedBatches: number
    totalBatches: number
    importedUsers: number
    totalUsers: number
  } | null>(null)
  const [getSlackDetailsQuery] = useManualQuery<IGetSlackDetails, { id: string }>(getSlackDetails)
  const [slackIdsQuery] = useManualQuery<
    ISlackIds,
    {
      limit: number
      nextToken?: string
    }
  >(slackIds)
  const [getGoogleScopesQuery] = useManualQuery<IGetGoogleScopes, { accessToken: string }>(getGoogleScopes)
  const [getAllUserListShortPaginatedQuery] = useManualQuery<
    IGetAllUserListShortPaginatedData,
    {
      limit: number
      nextToken?: string
    }
  >(getAllUserListShortPaginated)

  // When the Microsoft group/team stored in msTeamId no longer exists (deleted from the tenant),
  // Graph API returns Request_ResourceNotFound. This ref tracks the fallback so all MS functions
  // automatically query the entire tenant instead of the stale group.
  const msGroupNotFoundRef = useRef(false)
  const getEffectiveMsTeamId = (tenantId: string) => {
    if (msGroupNotFoundRef.current) return null
    return authCompany.msTeamId && authCompany.msTeamId !== tenantId ? authCompany.msTeamId : null
  }

  // Refs for CSV rowHook - the library may snapshot the hook, so refs ensure
  // the validation function always reads the latest async-loaded data.
  const notImportedUsersRef = useRef<IUserToImportData[]>([])
  const activeUsersRef = useRef<IGetUserListShort[]>([])
  const departmentsRef = useRef<{ id: string; name: string }[]>([])
  const locationsRef = useRef<{ id: string; name: string }[]>([])
  const hasLoadedNotImportedUsersRef = useRef(false)
  useEffect(() => {
    notImportedUsersRef.current = notImportedUsers
  }, [notImportedUsers])
  useEffect(() => {
    activeUsersRef.current = activeUsers
  }, [activeUsers])
  useEffect(() => {
    departmentsRef.current = departments.map((d) => ({ id: d.id, name: d.name }))
  }, [departments])
  useEffect(() => {
    locationsRef.current = locations.map((l) => ({ id: l.id, name: l.name }))
  }, [locations])
  useEffect(() => {
    hasLoadedNotImportedUsersRef.current = hasLoadedNotImportedUsers
  }, [hasLoadedNotImportedUsers])
  useEffect(() => {
    setNoOfUsersToImport(importAllUsers ? totalNumberOfUsers - activeUsers.length : tableRows.length)
  }, [importAllUsers, totalNumberOfUsers, activeUsers.length, tableRows.length])

  useEffect(() => {
    // TODO: Do we need this part of microsoft code here
    if (platform === 'microsoft') {
      getTotalUsersInMicrosoftTenant()
    }

    if (platform === 'google') {
      shouldEnableGoogleAdminPermissions ? fetchFromGoogleApiWithFeatureFlag() : fetchFromGoogleApi()
    }

    track('IMPORT_USERS_PAGE_VISITED', {
      platform,
      tokenExists: checkForTokenExistence(platform),
    })
  }, [shouldEnableGoogleAdminPermissions])

  useEffect(() => {
    if (msAuth) {
      const tenantId = msAuth.getTenantId() || 'common'
      setAdminConsentUrl(`https://login.microsoftonline.com/${tenantId}/adminconsent?client_id=${process.env.REACT_APP_MICROSOFT_CLIENT_ID}`)
    }
  }, [msAuth])

  useEffect(() => {
    fetchActiveUsers()
    if (eventsNotifications[correlationId]) {
      setCorrelationExist(true)
    }
    if (!eventsNotifications[correlationId] && correlationExist) {
      setCorrelationExist(false)
      setCorrelationId('')
    }
  }, [eventsNotifications])

  useEffect(() => {
    setImportUsersAutomatically(Boolean(authCompany?.importUsersAutomatically))
  }, [authCompany])

  useEffect(() => {
    if (platform === 'slack') {
      getSlackUserDetails()
    }
  }, [authUser])

  useEffect(() => {
    const calculatedNumberOfPossibleActiveUsersAfterImport = importAllUsers
      ? totalNumberOfUsers - activeUsers.filter((user) => user.status !== 'ACTIVE').length
      : activeUsers.filter((user) => user.status === 'ACTIVE').length + tableRows.length
    setTotalUsersAfterImport(calculatedNumberOfPossibleActiveUsersAfterImport)
  }, [totalNumberOfUsers, importAllUsers, activeUsers.length, tableRows.length])

  useEffect(() => {
    if (totalUsersAfterImport > 0) {
      getUpcomingInvoiceInfo(totalUsersAfterImport)
    }
  }, [totalUsersAfterImport])

  useEffect(() => {
    if (totalNumberOfUsers > AUTOMATIC_USERS_IMPORT_LIMIT) {
      setImportAllUsers(false)
      setShouldEnableImportAll(featureFlags.includes(FeatureFlagEnum.importUsers))
    } else {
      setShouldEnableImportAll(true)
    }
  }, [featureFlags, totalNumberOfUsers, activeUsers.length])

  useEffect(() => {
    if (platform === 'slack' && slackUserDetails) {
      fetchFromSlackApi(isBoolean(authCompany?.shouldImportGuestUsers) ? authCompany?.shouldImportGuestUsers : false)
    }
  }, [slackUserDetails])

  const getUpcomingInvoiceInfo = async (noOfUsersToImport: number) => {
    if (noOfUsersToImport === 0) {
      return
    }
    setIsLoadingUpcomingInvoiceInfo(true)
    try {
      let info: IUpcomingInvoiceInformation | null = null
      if (authCompany?.plan === SubscriptionPlanEnum.freemium) {
        info = zeroUpcomingInvoiceInformation
      } else {
        info = await Api.post('/stripe/upcoming-invoice-information', {
          quantity: noOfUsersToImport,
        })
      }
      if (info) {
        setUpcomingInvoiceInfo(info)
        setIsLoadingUpcomingInvoiceInfo(false)
      }
    } catch (error) {
      setIsLoadingUpcomingInvoiceInfo(false)
    }
  }

  const getSlackUserDetails = async (id = authUser.id) => {
    try {
      const response = await getSlackDetailsQuery({ variables: { id } })
      if (!response.data?.slackId) {
        throw new Error('No slack user details found')
      }
      setSlackUserDetails(response.data.slackId)
    } catch (err) {
      // Retry with unprefixed id
      // TODO: @slobodan remove this after migration
      if (id === authUser.id) {
        return getSlackUserDetails(authUser.id.replace('slack-', ''))
      }
      return err
    }
  }

  const checkForTokenExistence = (platform: Platform) => {
    try {
      let tokens
      switch (platform) {
        case 'microsoft': {
          tokens = msAuth.getWebAppTokens()
          break
        }
        case 'slack': {
          tokens = slackAuth.getBotToken()
          break
        }
        case 'google': {
          tokens = googleAuth.getAccessToken()
          break
        }
      }
      return Boolean(tokens)
    } catch (e) {
      return false
    }
  }

  const getMsNumberOfUsers = async (accessToken, teamId: string | null = null) => {
    const numberOfUsers = await msAuth.getTotalUsers(accessToken as string, teamId)
    setTotalNumberOfUsers(numberOfUsers)
  }

  const getBasicMsToken = () => {
    setShowMsLoginRequiredModal(false)
    msAuth
      .signin(['user.readbasic.all', 'team.readbasic.all'])
      .then(([tokens, msUser, tenantId]) => {
        if (tenantId !== msTenantId) {
          wrongMicrosoftAccount()
          return
        }
        return getMsNumberOfUsers(tokens.accessToken, authCompany.msTeamId === msTenantId ? null : authCompany.msTeamId)
      })
      .catch((error) => {
        errorNotificationHandler(error, 'microsoft', getBasicMsToken)
      })
  }

  const wrongMicrosoftAccount = () => {
    msAuth.removeTokens()
    notification.error({
      message: formatMessage({ id: 'error.microsoft.wrongAccount' }),
      description: (
        <div>
          <Paragraph>{formatMessage({ id: 'errors.microsoft.wrongMicrosoftAccountDescription' }, { email: authUser.email })}</Paragraph>
          <Button onClick={() => getBasicMsToken()}>{formatMessage({ id: 'error.microsoft.logInWithDifferentAccount' })}</Button>
        </div>
      ),
      duration: 0,
    })
  }

  const checkAutoImportAutomatically = (value: boolean) => {
    if (!value) return

    if (!sessionStorage.getItem('msWebAppTokensExpiration') || isAfter(new Date(), new Date(sessionStorage.getItem('msWebAppTokensExpiration') as string))) {
      setShowMsLoginAdditionalScopesRequiredModal(true)
    } else {
      const msWebAppTokensAccessToken = sessionStorage.getItem('msWebAppTokensAccessToken') as string
      const scopes = msAuth.getPermissions(msWebAppTokensAccessToken)
      if (!scopes.toLowerCase().includes('group.read.all')) {
        return setShowMsLoginAdditionalScopesRequiredModal(true)
      }
    }
  }

  const { isValidatingGooglePermissions, adminTokenHash, pendingAutoImportValidation, setPendingAutoImportValidation, setAdminTokenHash, checkAutoImportAutomaticallyGoogle } =
    useGoogleAutoImportValidation({
      setShowGoogleLoginRequiredModal,
      setShowGoogleInsufficientPermissionsModal,
      onValidationFail: () => setImportUsersAutomatically(false),
      onUnexpectedError: (error: Error) => {
        notification.error({
          message: formatMessage({ id: 'error.google.validationFailed' }),
          description: error.message || 'An unexpected error occurred',
          duration: 0,
        })
      },
    })

  const getWebAppTokens = async (tenantId: string) => {
    try {
      await msAuth.getMSWebAppTokens(tenantId, addMinutes(new Date(), 15).toString(), 'offline_access user.read group.read.all')
      setImportUsersAutomatically(importUsersAutomatically)
      setShowMsLoginAdditionalScopesRequiredModal(false)
    } catch (error) {
      logger.warning(error)
      setShowMsLoginAdditionalScopesRequiredModal(false)
      setMsAdminConsentRequired(true)
    }
  }

  const getTotalUsersInMicrosoftTenant = async () => {
    try {
      const tenantId = await Api.get<string>('/microsoft/get-tenant')
      setMsTenantId(tenantId)

      logger.info(tenantId)
      logger.info(authCompany.msTeamId || 'NO TEAM ID')

      const accessToken = (await msAuth.refreshTokens()).accessToken
      const teamId = getEffectiveMsTeamId(tenantId)
      await getMsNumberOfUsers(accessToken, teamId)
    } catch (err) {
      if (err?.data?.error?.code === 'Request_ResourceNotFound' && !msGroupNotFoundRef.current) {
        logger.warning('Microsoft group/team not found, falling back to tenant-level query')
        msGroupNotFoundRef.current = true
        return getTotalUsersInMicrosoftTenant()
      }
      if (err?.data?.error?.code === 'Authorization_RequestDenied') {
        setMsAdminConsentRequired(true)
        setShowAdminConsentWarning(true)
        return
      }
      if (
        err?.data?.error?.code === 'InvalidAuthenticationToken' ||
        err?.includes('FAILED CODE OR TOKEN FETCH') ||
        err?.includes('invalid_grant') ||
        (err?.includes('invalid_request') && err?.includes('refresh_token'))
      ) {
        return setShowMsLoginRequiredModal(true)
      }

      captureException(err)
      errorNotificationHandler(err, 'microsoft', () => {
        getTotalUsersInMicrosoftTenant()
      })
      setMsAdminConsentRequired(true)
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleErrorNotification = (error: any, correlationId?: string, title?: string): void => {
    const description = correlationId
      ? formatMessage({ id: 'notifications.handleSubmitError' }, { correlationId })
      : error.response?.data?.message
        ? error.response?.data?.message
        : error.message
          ? error.message
          : JSON.stringify(error)
    notification.error({
      message: title ? title : formatMessage({ id: 'error.generic' }),
      description,
      duration: 0,
    })
    Sentry.captureException(JSON.stringify(error))
  }

  const fetchSlackUsersInSystem = async (nextToken?: string, limit = 200, slackUsers: ISlackIdUser[] = []) => {
    const usersInSystem = await slackIdsQuery({ variables: { nextToken, limit } })
    if (!usersInSystem.data || usersInSystem.error) throw usersInSystem.error
    const users = slackUsers.concat(usersInSystem.data.slackIds.users)

    if (usersInSystem.data.slackIds.nextToken) {
      return await fetchSlackUsersInSystem(usersInSystem.data.slackIds.nextToken, limit, users)
    }

    return users
  }

  const fetchFromSlackApi = async (shouldImportGuestUsers = true): Promise<void> => {
    try {
      let slackUsers: ISlackUser[] = []
      try {
        slackAuth.getBotToken()
        slackUsers = await slackAuth.getUsersList()
        // this is preventing the case when user manually changes token in local storage(and thus pulls users from other team):
        if (slackUsers[0].team_id !== slackUserDetails?.slackTeamId) {
          logger.error('WRONG SLACK TEAM ID', { slackUsers: JSON.stringify(slackUsers), slackUserDetails: JSON.stringify(slackUserDetails) })
          wrongSlackAccount()
          return
        }
      } catch (error) {
        if (error?.error === 'not_authed' || error?.error === 'invalid_auth' || error?.error === 'missing_scope' || error?.error === 'token_revoked') {
          setShowSlackLoginRequiredModal(true)
        } else if (error?.message === 'no_tokens') {
          errorNotificationHandler(error, 'slack', getSlackTokens)
        } else {
          handleErrorNotification({ message: `Slack error: ${error?.error}${error.needed ? ` ${error.needed}` : ''}` })
        }
        return
      }
      const activeUsers = slackUsers
        .filter((user) => !user.deleted)
        .filter((user) => !user.is_bot)
        .filter((user) => !user.is_ultra_restricted)
        .filter((user) => user.id !== 'USLACKBOT')

      if (shouldImportGuestUsers) {
        setTotalNumberOfUsers(activeUsers.filter((user) => !user.is_restricted).length)
      } else {
        setTotalNumberOfUsers(activeUsers.length)
      }

      const usersInSystem = await fetchSlackUsersInSystem()

      const uniqueList = activeUsers.filter((user) => {
        const isUserExists = usersInSystem.find((u) => u.slackUserId === user.id)
        if (!isUserExists) {
          return user
        }

        return null
      })

      setNotImportedUsers(
        uniqueList.map((user) => {
          return {
            label: `${user.real_name} (${user.name})`,
            value: JSON.stringify({
              id: user.id,
              name: user.name,
              realName: user.real_name,
              mail: user.profile?.email || '',
              imageUrl: user.profile?.image_48 || '',
            }),
          }
        })
      )
      setHasLoadedNotImportedUsers(true)
    } catch (error) {
      logger.error('ERROR FETCH FROM SLACK API', error)
    }
  }

  const handleChangeGuestUsersImport = async (v: boolean) => {
    await fetchFromSlackApi(v)
  }

  const wrongSlackAccount = () => {
    slackAuth.removeTokens()
    notification.error({
      message: formatMessage({ id: 'error.slack.wrongAccount' }),
      description: (
        <div>
          <Paragraph>{formatMessage({ id: 'errors.slack.wrongSlackAccountDescription' })}</Paragraph>
          <Button onClick={() => getSlackTokens()}>{formatMessage({ id: 'error.slack.logInWithDifferentAccount' })}</Button>
        </div>
      ),
      duration: 0,
    })
    setShowSlackLoginRequiredModal(false)
  }

  const getSlackTokens = () => {
    slackAuth
      .signin(SLACK_REQUIRED_BOT_SCOPES, SLACK_REQUIRED_USER_SCOPES)
      .then(async (response) => {
        if (
          (!response.enterprise && response.team.id !== authCompany.organizationId) ||
          // In case that slack company moved from workspace to enterprise and we didn't chnage organizationId to enterpriseId (VT-9485)
          (response.enterprise && response.enterprise?.id !== authCompany.organizationId && response.team.id !== authCompany.organizationId)
        ) {
          logger.error('WRONG SLACK ACCOUNT', JSON.stringify(response))
          wrongSlackAccount()
          return
        }
        slackAuth.setBotToken(response.access_token)
        track('IMPORT_USERS_LOGIN_CLICKED', { platform }) // TODO: add this tracking for google platform
        slackAuth.setUserToken(response.authed_user.access_token)
        try {
          await Api.post('/slack/update-tokens', {
            token: response.authed_user.access_token,
            botToken: response.access_token,
          })
        } catch (error) {
          logger.error('UPDATE TOKEN ERROR', error.response)
        }
        setShowSlackLoginRequiredModal(false)
        await fetchFromSlackApi(isBoolean(authCompany?.shouldImportGuestUsers) ? authCompany?.shouldImportGuestUsers : true)
      })
      .catch((error) => {
        logger.warning('SLACK SIGNIN ERROR', error)
        handleErrorNotification({ message: 'To proceed, please login to your Slack account' }, undefined, 'Missing Slack Scopes')
      })
  }

  const getGoogleTokens = (withConsent = false) => {
    logger.info('Google tokens: Starting authentication', { withConsent })

    googleAuth
      .signin(withConsent)
      .then(() => {
        logger.info('Google tokens: Authentication successful')
        setShowGoogleLoginRequiredModal(false)

        // If user was trying to enable auto-import when login was required, re-run validation
        if (pendingAutoImportValidation) {
          logger.info('Google tokens: Re-running auto-import validation after OAuth')
          setPendingAutoImportValidation(false)
          checkAutoImportAutomaticallyGoogle(true)
          return
        }

        wait(500)
        logger.info('Google tokens: Proceeding to fetch users', {
          shouldEnableGoogleAdminPermissions,
        })
        shouldEnableGoogleAdminPermissions ? fetchFromGoogleApiWithFeatureFlag() : fetchFromGoogleApi(true)
      })
      .catch((error) => {
        logger.error('Google tokens: Authentication failed', { error: error.message })
        throw error
      })
  }

  const fetchFromGoogleApi = async (ignorePermission = false): Promise<void> => {
    logger.info('Google API: Starting fetchFromGoogleApi', { ignorePermission })

    const googleTokenExpiration = sessionStorage.getItem('googleTokenExpiration')
    logger.info('Google API: Token expiration check', {
      googleTokenExpiration,
      isExpired: !googleTokenExpiration || isAfter(new Date(), new Date(googleTokenExpiration)),
    })

    if (!googleTokenExpiration || isAfter(new Date(), new Date(googleTokenExpiration))) {
      logger.warning('Google API: Token expired, showing login modal')
      setShowGoogleLoginRequiredModal(true)
    } else {
      logger.info('Google API: Token valid, proceeding with API calls')
      setShowGoogleLoginRequiredModal(false)

      const googleScopesResponse = await getGoogleScopesQuery({ variables: { accessToken: googleAuth.getAccessToken() } })
      if (!googleScopesResponse.data || googleScopesResponse.error) {
        logger.error('Google API: Failed to get Google scopes', { error: googleScopesResponse.error })
        throw googleScopesResponse.error
      }

      const googleApiScopes = googleScopesResponse.data.getGoogleTokenInfo.scopes as string[]
      const vtGooogleIntegrationScopes = googleScopesResponse.data.getGoogleIntegrationSettings?.scopes

      logger.info('Google API: Retrieved scopes', {
        googleApiScopes,
        vtGooogleIntegrationScopes,
        hasReadUserPermission: googleApiScopes?.includes(GOOGLE_READ_USER_PERMISSION),
      })

      setGoogleScopes({
        googleApiScopes,
        vtGooogleIntegrationScopes,
      })

      if (googleApiScopes?.includes(GOOGLE_READ_USER_PERMISSION) && !vtGooogleIntegrationScopes?.includes(GOOGLE_READ_USER_PERMISSION)) {
        logger.info('Google API: Updating Google integration settings with new scope')
        const userInfo = googleAuth.getSignedInUser()
        logger.info('Google API: User info for integration', {
          userDomain: userInfo.hd,
          scopesJoined: googleApiScopes.join(' '),
        })

        await Api.post('/core/event', {
          eventType: 'GOOGLE_INTEGRATION',
          eventGroup: 'INTEGRATION',
          scope: googleApiScopes.join(' '),
          domainName: userInfo.hd,
        })
        logger.info('Google API: Google integration event posted successfully')
      }

      try {
        let googleUsers
        const shouldUseAdminAPI = !ignorePermission && googleApiScopes?.includes(GOOGLE_READ_USER_PERMISSION)

        logger.info('Google API: Determining user fetch method', {
          shouldUseAdminAPI,
          ignorePermission,
          hasReadUserPermission: googleApiScopes?.includes(GOOGLE_READ_USER_PERMISSION),
        })

        if (shouldUseAdminAPI) {
          logger.info('Google API: Using Admin API to fetch users')
          await googleAuth.tokenInfo()
          const token = googleAuth.getAccessToken()
          try {
            googleUsers = await Api.post('/google/get-google-admin-user-list', {
              accessToken: token,
            })
            logger.info('Google API: Admin API users fetched successfully', {
              userCount: googleUsers.length,
            })
            logger.info('Google API: Admin API users fetched successfully', {
              users: JSON.stringify(googleUsers),
            })
          } catch (error) {
            logger.warning('Google API: Admin API failed, falling back to People API', {
              error: error.message,
              reason: error.reason,
            })
            // If admin add scope GOOGLE_READ_USER_PERMISSION from google workspace, this not meen that they can use this permission
            // GOOGLE_READ_USER_PERMISSION can use only super admin from google workspace, or specific group (custom group) with this permission
            // In this case we need to fetch all users from google directory people api
            if (error.reason === 'forbidden' && error.message.includes('resource/api')) {
              googleUsers = await googleAuth.getUserListForImportFull()
              logger.info('Google API: People API users fetched after admin API failure', {
                userCount: googleUsers.length,
              })
            } else {
              throw error
            }
          }
        } else {
          logger.info('Google API: Using People API to fetch users')
          googleUsers = await googleAuth.getUserListForImportFull()
          logger.info('Google API: People API users fetched successfully', {
            userCount: googleUsers.length,
          })
          logger.info('Google API: People API users fetched successfully', {
            users: JSON.stringify(googleUsers),
          })
        }

        logger.info('Google API: Setting total number of users', {
          totalUsers: googleUsers.length,
        })
        setTotalNumberOfUsers((googleUsers.length as number) || 0)

        logger.info('Google API: Fetching users already in system')
        const usersInSystem = await fetchAllUsersInSystem()
        logger.info('Google API: Users in system fetched', {
          usersInSystemCount: usersInSystem.length,
          activeUsersInSystem: usersInSystem.filter((u) => u.status === 'ACTIVE').length,
          inactiveUsersInSystem: usersInSystem.filter((u) => u.status === 'INACTIVE').length,
          deletedUsersInSystem: usersInSystem.filter((u) => u.status === 'DELETED').length,
        })

        const uniqueList = googleUsers.filter((user) => {
          const isUserExists = usersInSystem.find((u) => u.status !== 'DELETED' && u.id === user.id)
          if (!isUserExists) {
            return user
          }
          return null
        })

        logger.info('Google API: Filtered unique users for import', {
          uniqueUsersCount: uniqueList.length,
          totalGoogleUsers: googleUsers.length,
          usersAlreadyInSystem: googleUsers.length - uniqueList.length,
        })

        setNotImportedUsers(
          uniqueList.map((user) => {
            return {
              label: `${user.name} (${user.name})`,
              value: JSON.stringify({
                id: user.id,
                name: user.name,
                realName: user.name,
                mail: user.email || '',
                imageUrl: user.image || '',
              }),
            }
          }) as IUserToImportData[]
        )
        setHasLoadedNotImportedUsers(true)

        logger.info('Google API: fetchFromGoogleApi completed successfully')
      } catch (error) {
        logger.error('Google API: Error in fetchFromGoogleApi', {
          error: error.message,
          errorCode: error.code,
          errorReason: error.reason,
        })

        if (error.message.includes('Not Authorized to access this resource/api') && googleApiScopes?.includes(GOOGLE_READ_USER_PERMISSION)) {
          logger.error('Google API: Not authorized despite having read user permission')
          notification.error({
            message: 'Google API Error',
            description: (
              <>
                <Paragraph>{formatMessage({ id: 'components.importUsersForm.googleAPIErrorMessage' })}</Paragraph>
                <Paragraph code copyable>
                  {error.message}
                </Paragraph>
                {error.code && <Paragraph>{error.code}</Paragraph>}
              </>
            ),
            duration: 0,
          })
          return
        }

        if (error.message.includes('400') && googleApiScopes?.includes(GOOGLE_READ_USER_PERMISSION)) {
          logger.info('Google API: Got 400 error, retrying with ignorePermission=true')
          return fetchFromGoogleApi(true)
        }

        if (error.message === 'GOOGLE_DIRECTORY_SHARING_DISABLED' || error.message === 'GOOGLE_DIRECTORY_PERMISSION_DENIED') {
          logger.warning('Google API: Directory sharing disabled or permission denied', { error: error.message })
          track('GOOGLE_ERROR_DIRECTORY_SHARING_DISABLED', { page: 'ImportUsersPage', platform: 'google', error: error.message })
          setShowEnableGoogleApiModal(true)
          return
        }

        if (error.message === 'no_tokens') {
          logger.error('Google API: No tokens available')
          errorNotificationHandler(error, 'google', getGoogleTokens)
          return
        }

        logger.error('Google API: Unhandled error', { error: error.message })
        handleErrorNotification({ message: `Google error: ${error?.error}${error.needed ? ` ${error.needed}` : ''}` })
      }
    }
  }

  const fetchFromGoogleApiWithFeatureFlag = async (): Promise<void> => {
    logger.info('Google API (Feature Flag): Starting fetchFromGoogleApiWithFeatureFlag')

    const googleTokenExpiration = sessionStorage.getItem('googleTokenExpiration')
    logger.info('Google API (Feature Flag): Token expiration check', {
      googleTokenExpiration,
      isExpired: !googleTokenExpiration || isAfter(new Date(), new Date(googleTokenExpiration)),
    })

    if (!googleTokenExpiration || isAfter(new Date(), new Date(googleTokenExpiration))) {
      logger.warning('Google API (Feature Flag): Token expired, showing login modal')
      setShowGoogleLoginRequiredModal(true)
    } else {
      logger.info('Google API (Feature Flag): Token valid, proceeding with API calls')
      setShowGoogleLoginRequiredModal(false)

      const googleScopesResponse = await getGoogleScopesQuery({ variables: { accessToken: googleAuth.getAccessToken() } })
      if (!googleScopesResponse.data || googleScopesResponse.error) {
        logger.error('Google API (Feature Flag): Failed to get Google scopes', { error: googleScopesResponse.error })
        throw googleScopesResponse.error
      }

      const googleApiScopes = googleScopesResponse.data.getGoogleTokenInfo.scopes as string[]
      const vtGooogleIntegrationScopes = googleScopesResponse.data.getGoogleIntegrationSettings?.scopes

      logger.info('Google API (Feature Flag): Retrieved scopes', {
        googleApiScopes,
        vtGooogleIntegrationScopes,
        hasReadUserPermission: googleApiScopes?.includes(GOOGLE_READ_USER_PERMISSION),
      })

      setGoogleScopes({
        googleApiScopes,
        vtGooogleIntegrationScopes,
      })

      if (googleApiScopes?.includes(GOOGLE_READ_USER_PERMISSION) && !vtGooogleIntegrationScopes?.includes(GOOGLE_READ_USER_PERMISSION)) {
        logger.info('Google API (Feature Flag): Updating Google integration settings with new scope')
        const userInfo = googleAuth.getSignedInUser()
        logger.info('Google API (Feature Flag): User info for integration', {
          userDomain: userInfo.hd,
          scopesJoined: googleApiScopes.join(' '),
        })

        await Api.post('/core/event', {
          eventType: 'GOOGLE_INTEGRATION',
          eventGroup: 'INTEGRATION',
          scope: googleApiScopes.join(' '),
          domainName: userInfo.hd,
        })
        logger.info('Google API (Feature Flag): Google integration event posted successfully')
      }

      try {
        logger.info('Google API (Feature Flag): Calling tokenInfo and getting access token')
        await googleAuth.tokenInfo()
        const token = googleAuth.getAccessToken()
        logger.info('Google API (Feature Flag): About to call getGoogleAdminDirectoryUsersList')
        await getGoogleAdminDirectoryUsersList(token)
        logger.info('Google API (Feature Flag): fetchFromGoogleApiWithFeatureFlag completed successfully')
      } catch (error) {
        logger.warning('Google API (Feature Flag): Error in fetchFromGoogleApiWithFeatureFlag', {
          error: error.message,
          errorCode: error.code,
          errorReason: error.reason,
          responseData: error?.response?.data,
        })

        if (
          error?.response?.data?.reason === 'forbidden' ||
          error.response?.data?.message === 'Insufficient Permission' ||
          error.message.includes('Insufficient Permission') ||
          (error.message.includes('Not Authorized to access this resource/api') && !googleApiScopes?.includes(GOOGLE_READ_USER_PERMISSION))
        ) {
          logger.warning('Google API (Feature Flag): Insufficient permissions, handling admin permissions')
          handleInsufficientGoogleAdminPermissions()
          return
        }

        if (error.message.includes('Not Authorized to access this resource/api') && googleApiScopes?.includes(GOOGLE_READ_USER_PERMISSION)) {
          logger.error('Google API (Feature Flag): Not authorized despite having read user permission')
          notification.error({
            message: 'Google API Error',
            description: (
              <>
                <Paragraph>{formatMessage({ id: 'components.importUsersForm.googleAPIErrorMessage' })}</Paragraph>
                <Paragraph code copyable>
                  {error.message}
                </Paragraph>
                {error.code && <Paragraph>{error.code}</Paragraph>}
              </>
            ),
            duration: 0,
          })
          return
        }

        if (error.message.includes('400') && googleApiScopes?.includes(GOOGLE_READ_USER_PERMISSION)) {
          logger.info('Google API (Feature Flag): Got 400 error, retrying')
          return fetchFromGoogleApiWithFeatureFlag()
        }

        if (error.message === 'GOOGLE_DIRECTORY_SHARING_DISABLED' || error.message === 'GOOGLE_DIRECTORY_PERMISSION_DENIED') {
          logger.warning('Google API (Feature Flag): Directory sharing disabled or permission denied', { error: error.message })
          track('GOOGLE_ERROR_DIRECTORY_SHARING_DISABLED', { page: 'ImportUsersPage', platform: 'google', error: error.message })
          setShowEnableGoogleApiModal(true)
          return
        }

        if (error.message === 'no_tokens') {
          logger.error('Google API (Feature Flag): No tokens available')
          errorNotificationHandler(error, 'google', getGoogleTokens)
          return
        }

        logger.error('Google API (Feature Flag): Unhandled error', { error: error.message })
        handleErrorNotification({ message: `Google error: ${error?.error}${error.needed ? ` ${error.needed}` : ''}` })
      }
    }
  }

  const getGoogleAdminDirectoryUsersList = async (accessToken: string) => {
    logger.info('Google Admin Directory: Starting getGoogleAdminDirectoryUsersList')

    try {
      const googleUsers = await Api.post('/google/get-google-admin-user-list', { accessToken })
      logger.info('Google Admin Directory: Users fetched successfully', {
        userCount: googleUsers.length,
      })
      logger.info('Google Admin Directory: Users fetched successfully', {
        users: googleUsers,
      })

      setTotalNumberOfUsers((googleUsers.length as number) || 0)

      logger.info('Google Admin Directory: Fetching users already in system')
      const usersInSystem = await fetchAllUsersInSystem()
      logger.info('Google Admin Directory: Users in system fetched', {
        usersInSystemCount: usersInSystem.length,
        activeUsersInSystem: usersInSystem.filter((u) => u.status === 'ACTIVE').length,
        inactiveUsersInSystem: usersInSystem.filter((u) => u.status === 'INACTIVE').length,
        deletedUsersInSystem: usersInSystem.filter((u) => u.status === 'DELETED').length,
      })

      const uniqueList = googleUsers.filter((user) => {
        const isUserExists = usersInSystem.find((u) => u.status !== 'DELETED' && u.id === user.id)
        if (!isUserExists) {
          return user
        }
        return null
      })

      logger.info('Google Admin Directory: Filtered unique users for import', {
        uniqueUsersCount: uniqueList.length,
        totalGoogleUsers: googleUsers.length,
        usersAlreadyInSystem: googleUsers.length - uniqueList.length,
      })

      setNotImportedUsers(
        uniqueList.map((user) => {
          return {
            label: `${user.name} (${user.name})`,
            value: JSON.stringify({
              id: user.id,
              name: user.name,
              realName: user.name,
              mail: user.email || '',
              imageUrl: user.image || '',
            }),
          }
        }) as IUserToImportData[]
      )
      setHasLoadedNotImportedUsers(true)

      logger.info('Google Admin Directory: getGoogleAdminDirectoryUsersList completed successfully')
    } catch (error) {
      logger.error('Google Admin Directory: Error in getGoogleAdminDirectoryUsersList', {
        error: error.message,
        errorCode: error.code,
        errorReason: error.reason,
        responseData: error?.response?.data,
      })
      throw error
    }
  }

  const handleInsufficientGoogleAdminPermissions = async () => {
    logger.info('Google Admin Permissions: Starting handleInsufficientGoogleAdminPermissions')

    try {
      logger.info('Google Admin Permissions: Checking for existing admin token')
      const existingGoogleAdminToken = await Api.get('/google/admin-token')
      logger.info('Google Admin Permissions: Found existing admin token, using it')
      await getGoogleAdminDirectoryUsersList(existingGoogleAdminToken as string)
    } catch (err) {
      logger.error('Google Admin Permissions: Error getting existing admin token', {
        error: err.message,
        errorCode: err.response?.data?.code,
      })

      if (err.response?.data?.code === NotFound.code) {
        logger.info('Google Admin Permissions: No existing admin token found, creating new hash')
        let hash = sessionStorage.getItem('adminTokenHash')
        if (!hash) {
          logger.info('Google Admin Permissions: No hash in session, creating new one')
          const response = (await Api.post('/google/admin-token-hash', {})) as { hash: string }
          hash = response.hash
          sessionStorage.setItem('adminTokenHash', hash)
          logger.info('Google Admin Permissions: New admin token hash created and stored')
        } else {
          logger.info('Google Admin Permissions: Using existing hash from session')
        }
        setAdminTokenHash(hash)
        setShowRequestGoogleAdminPermissionsModal(true)
      } else {
        logger.error('Google Admin Permissions: Unexpected error', { error: err.message })
        throw err
      }
    }
  }

  const getGoogleAdminExtraPermissionsLink = () => {
    const hash = sessionStorage.getItem('adminTokenHash')
    const link = `https://app.vacationtracker.io/vt-google-permissions#${hash}`
    logger.info('Google Admin Permissions: Generated permissions link', {
      hasHash: !!hash,
      linkGenerated: !!link,
    })
    return link
  }

  const fetchActiveUsers = async () => {
    const usersInSystem = await fetchAllUsersInSystem()
    setActiveUsers(usersInSystem)
  }

  const fetchAllUsersInSystem = async (nextToken?: string, limit = 200, shortUsers: IGetUserListShort[] = []): Promise<IGetUserListShort[]> => {
    const usersInSystem = await getAllUserListShortPaginatedQuery({ variables: { nextToken, limit } })
    if (!usersInSystem.data || usersInSystem.error) throw usersInSystem.error

    const users = shortUsers.concat(usersInSystem.data.getAllUserListPaginated.users as IGetUserListShort[])

    if (usersInSystem.data.getAllUserListPaginated.nextToken) {
      return await fetchAllUsersInSystem(usersInSystem.data.getAllUserListPaginated.nextToken, limit, users)
    }

    // We do not want deleted users as they are not in the directory anymore
    return users.filter((user) => user.status !== 'DELETED')
  }

  const onSearch = async (value: string) => {
    if (platform === 'microsoft') {
      return await microsoftOnUserSearch(value)
    }
    return []
  }

  const microsoftOnUserSearch = async (value: string): Promise<IAutocompleteOption[]> => {
    if (value.length < 1) {
      return []
    }
    try {
      const msTeamId = getEffectiveMsTeamId(msTenantId)
      const msUsers = await msAuth.searchUserByEmailAndDisplayname(value, sessionStorage.getItem('msTokensAccessToken') as string, msTeamId)
      const filteredUsers = filterUsers('microsoft', msUsers)
      return filteredUsers.map((user) => {
        const userName = getName(user)
        const userEmail = getEmail(user)

        return {
          label: `${userName} (${userEmail})`,
          value: JSON.stringify({
            id: user.id,
            name: userName,
            mail: userEmail,
          }),
        }
      })
    } catch (err) {
      if (err?.data?.error?.code === 'Request_ResourceNotFound' && !msGroupNotFoundRef.current) {
        msGroupNotFoundRef.current = true
        return microsoftOnUserSearch(value)
      }
      if (
        err?.data?.error?.code === 'InvalidAuthenticationToken' ||
        err?.includes('FAILED CODE OR TOKEN FETCH') ||
        err?.includes('invalid_grant') ||
        (err?.includes('invalid_request') && err?.includes('refresh_token'))
      ) {
        setShowMsLoginRequiredModal(true)
        return []
      }

      captureException(err)

      errorNotificationHandler(err, 'microsoft', () => {
        getTotalUsersInMicrosoftTenant()
      })
      setMsAdminConsentRequired(true)
      return []
    }
  }

  const filterUsers = (platform: string, msUsers: IMicrosoftUser[]): IMicrosoftUser[] => {
    if (platform === 'microsoft') {
      return msUsers
        .map((msUser) => {
          const existingUser = activeUsers.find((u) => u.id === vtUserId(msUser.id))
          const alreadySelected = selectedUsers.find((u) => u.id === msUser.id)
          if ((!existingUser || existingUser.status === 'DELETED') && !alreadySelected) {
            return msUser
          }

          return false
        })
        .filter(Boolean) as IMicrosoftUser[]
    }

    return []
  }

  const onFinish = async (importAllUsers: boolean, selectedUsers: IAutocompleteOptionValue[], configData: any) => {
    const newUsers = selectedUsers.map((user) => {
      return {
        userId: platform === 'google' ? user.id.substring(7) : user.id,
        name: user.name,
        email: user.mail,
        ...(user.imageUrl && { imageUrl: user.imageUrl }),
        ...(user.departmentId && { teamId: user.departmentId }),
        ...(user.locationId && { locationId: user.locationId }),
        ...(user.sendWelcomeMessage !== undefined && { announceNewUsers: user.sendWelcomeMessage }),
        ...(user.birthday && { birthday: user.birthday }),
      }
    })
    if (platform === 'microsoft' && importAllUsers && isMsAdminConsentRequired && adminConsentUrl) {
      return setShowAdminConsentWarning(true)
    }

    setShowAdminConsentWarning(false)
    await form.validateFields()
    if (importAllUsers) {
      track('IMPORT_USERS_IMPORT_ALL', { platform, totalNumberOfUsers })
    } else {
      track('IMPORT_USERS_IMPORT_SELECTED', { platform, totalNumberOfUsers, selectedNumberOfUsers: noOfUsersToImport })
    }
    // Always send importAllUsers: false - the table is populated with users
    // so the backend processes them directly with per-user dept/loc overrides.
    // Auto-sync settings are saved via the importUsersAutomatically flag (separate).
    if (platform === 'microsoft') {
      return await importMicrosoftUsers(
        {
          ...configData,
          newUsers,
          importAllUsers: false,
        } as IImportData,
        importAllUsers
      )
    } else if (platform === 'slack') {
      importSlackUsers({
        ...configData,
        newUsers,
        organizationId: authCompany.organizationId,
        importAllUsers: false,
        totalUsers: totalNumberOfUsers,
      })
    } else if (platform === 'google') {
      importGoogleUsers({
        ...configData,
        newUsers,
        organizationId: authCompany.organizationId,
        importAllUsers: false,
        totalUsers: totalNumberOfUsers,
      })
    }
  }

  const importMicrosoftUsers = async (importData: IImportData, importAllUsers: boolean) => {
    if (!importData) return

    try {
      const accessToken = sessionStorage.getItem('msWebAppTokensAccessToken') || (sessionStorage.getItem('msTokensAccessToken') as string)
      const scopes = msAuth.getPermissions(accessToken)
      if (Boolean(importUsersAutomatically) && !scopes.toLowerCase().includes('group.read.all')) {
        return setShowMsLoginAdditionalScopesRequiredModal(true)
      }
      const refreshToken = sessionStorage.getItem('msWebAppTokensRefreshToken') || (sessionStorage.getItem('msTokensRefreshToken') as string)

      const users = importData.newUsers || []
      const body: IImportUsersEventBody & { msTeamId: string; token: string; version: number } = {
        eventType: 'IMPORT_USERS',
        eventGroup: 'BULK_ACTIONS',
        users,
        organizationId: authCompany.organizationId,
        msTeamId: authCompany.msTeamId as string,
        token: accessToken,
        importAllUsers: importData.importAllUsers,
        totalUsers: totalNumberOfUsers,
        importUsersAutomatically: Boolean(importUsersAutomatically),
        shouldProrate: importData.shouldProrate,
        prorateLeaveTypes: importData.shouldProrate ? importData.selectedLeaveTypes : [],
        defaultLocation: importData.selectedLocation,
        defaultDepartment: importData.selectedDepartment,
        announceNewUsers: importData.sendWelcomeMessageTo,
        refreshToken,
        version: 2,
      }
      if (!importAllUsers && body.importUsersAutomatically !== Boolean(authCompany?.importUsersAutomatically)) {
        delete body.importUsersAutomatically
      }

      if (users.length > IMPORT_BATCH_SIZE) {
        const batches = chunk(users, IMPORT_BATCH_SIZE)
        setImportProgress({ completedBatches: 0, totalBatches: batches.length, importedUsers: 0, totalUsers: users.length })

        let importedSoFar = 0
        for (let i = 0; i < batches.length; i++) {
          try {
            const batchBody = { ...body, users: batches[i] }
            if (i > 0) {
              delete batchBody.importUsersAutomatically
            }
            await Api.post('/core/event', batchBody)
            importedSoFar += batches[i].length
            setImportProgress({ completedBatches: i + 1, totalBatches: batches.length, importedUsers: importedSoFar, totalUsers: users.length })
          } catch (error) {
            setImportProgress(null)
            track('IMPORT_USERS_ERROR', { platform, error, batch: i + 1, totalBatches: batches.length })
            notification.error({
              message: formatMessage({ id: 'components.importUsersForm.importBatchErrorTitle' }),
              description: formatMessage({ id: 'components.importUsersForm.importBatchError' }, { imported: importedSoFar, total: users.length }),
              duration: 0,
            })
            if (importedSoFar > 0) {
              setImportedCount(importedSoFar)
              setImportCompleted(true)
            }
            return
          }
        }

        setImportProgress(null)
        setSelectedUsers([])
        setImportedCount(users.length)
        clearWizardState(authUser.id, authCompany?.id)
        setImportCompleted(true)
      } else {
        const response = await Api.post('/core/event', body)

        setSelectedUsers([])

        showNotification(importData.newUsers, importData.importAllUsers, response.correlationId as string, authCompany.platform)

        if (importAllUsers) {
          setEventsNotifications({
            ...eventsNotifications,
            [response.correlationId]: {
              eventType: 'IMPORT_USERS',
              payload: {
                totalUsers: totalNumberOfUsers - activeUsers.length,
              },
            },
          })
        }
        // Show done screen
        setImportedCount(tableRows.length)
        clearWizardState(authUser.id, authCompany?.id)
        setImportCompleted(true)
      }
    } catch (error) {
      track('IMPORT_USERS_ERROR', { platform, error })
      showErrorNotification(error)
    }
  }

  const importSlackUsers = async (importData) => {
    if (!importData) return
    const users = importData.newUsers || []
    const body: IImportUsersEventBody = {
      eventType: 'IMPORT_USERS',
      eventGroup: 'BULK_ACTIONS',
      users,
      organizationId: authCompany.organizationId,
      importAllUsers: importData.importAllUsers,
      totalUsers: totalNumberOfUsers,
      importUsersAutomatically: importUsersAutomatically,
      shouldProrate: importData.shouldProrate,
      prorateLeaveTypes: importData.shouldProrate ? importData.selectedLeaveTypes : [],
      shouldImportGuestUsers: importData.shouldImportGuestUsers,
      defaultLocation: importData.selectedLocation,
      defaultDepartment: importData.selectedDepartment,
      announceNewUsers: importData.sendWelcomeMessageTo,
    }

    if (!importAllUsers && body.importUsersAutomatically !== Boolean(authCompany?.importUsersAutomatically)) {
      delete body.importUsersAutomatically
    }

    try {
      if (users.length > IMPORT_BATCH_SIZE) {
        const batches = chunk(users, IMPORT_BATCH_SIZE)
        setImportProgress({ completedBatches: 0, totalBatches: batches.length, importedUsers: 0, totalUsers: users.length })

        let importedSoFar = 0
        for (let i = 0; i < batches.length; i++) {
          try {
            const batchBody = { ...body, users: batches[i] }
            if (i > 0) {
              delete batchBody.importUsersAutomatically
            }
            await Api.post('/core/event', batchBody)
            importedSoFar += batches[i].length
            setImportProgress({ completedBatches: i + 1, totalBatches: batches.length, importedUsers: importedSoFar, totalUsers: users.length })
          } catch (error) {
            setImportProgress(null)
            track('IMPORT_USERS_ERROR', { platform, error, batch: i + 1, totalBatches: batches.length })
            notification.error({
              message: formatMessage({ id: 'components.importUsersForm.importBatchErrorTitle' }),
              description: formatMessage({ id: 'components.importUsersForm.importBatchError' }, { imported: importedSoFar, total: users.length }),
              duration: 0,
            })
            if (importedSoFar > 0) {
              setImportedCount(importedSoFar)
              setImportCompleted(true)
            }
            return
          }
        }

        setImportProgress(null)
        setImportedCount(users.length)
        clearWizardState(authUser.id, authCompany?.id)
        setImportCompleted(true)
      } else {
        const response = await Api.post('/core/event', body)
        showNotification(importData.newUsers, importData.importAllUsers, response.correlationId as string, authCompany.platform)
        // Show done screen
        setImportedCount(tableRows.length)
        clearWizardState(authUser.id, authCompany?.id)
        setImportCompleted(true)
      }
    } catch (error) {
      track('IMPORT_USERS_ERROR', { platform, error })
      logger.error('error', error)
    }
  }

  const importGoogleUsers = async (importData) => {
    if (!importData) return
    const googleTokenExpiration = sessionStorage.getItem('googleTokenExpiration')
    if (!googleTokenExpiration || isAfter(new Date(), new Date(googleTokenExpiration))) {
      return setShowGoogleLoginRequiredModal(true)
    }
    await googleAuth.tokenInfo()
    const userInfo = googleAuth.getSignedInUser()
    const users = importData.newUsers || []
    const body: IImportUsersEventBody & { token: string; domainName?: string } = {
      eventType: 'IMPORT_USERS',
      eventGroup: 'BULK_ACTIONS',
      users,
      organizationId: authCompany.organizationId,
      token: googleAuth.getAccessToken(),
      refreshToken: googleAuth.getRefreshToken(),
      importAllUsers: importData.importAllUsers,
      totalUsers: totalNumberOfUsers,
      importUsersAutomatically: importUsersAutomatically,
      shouldProrate: importData.shouldProrate,
      prorateLeaveTypes: importData.selectedLeaveTypes,
      defaultLocation: importData.selectedLocation,
      defaultDepartment: importData.selectedDepartment,
      announceNewUsers: importData.sendWelcomeMessageTo,
      domainName: googleScopes?.googleApiScopes?.includes('https://www.googleapis.com/auth/admin.directory.user.readonly') ? userInfo.hd : undefined,
    }

    if (!importAllUsers && body.importUsersAutomatically !== Boolean(authCompany?.importUsersAutomatically)) {
      delete body.importUsersAutomatically
    }

    try {
      if (users.length > IMPORT_BATCH_SIZE) {
        const batches = chunk(users, IMPORT_BATCH_SIZE)
        setImportProgress({ completedBatches: 0, totalBatches: batches.length, importedUsers: 0, totalUsers: users.length })

        let importedSoFar = 0
        for (let i = 0; i < batches.length; i++) {
          try {
            const batchBody = { ...body, users: batches[i] }
            if (i > 0) {
              delete batchBody.importUsersAutomatically
            }
            await Api.post('/core/event', batchBody)
            importedSoFar += batches[i].length
            setImportProgress({ completedBatches: i + 1, totalBatches: batches.length, importedUsers: importedSoFar, totalUsers: users.length })
          } catch (error) {
            setImportProgress(null)
            track('IMPORT_USERS_ERROR', { platform, error, batch: i + 1, totalBatches: batches.length })
            notification.error({
              message: formatMessage({ id: 'components.importUsersForm.importBatchErrorTitle' }),
              description: formatMessage({ id: 'components.importUsersForm.importBatchError' }, { imported: importedSoFar, total: users.length }),
              duration: 0,
            })
            if (importedSoFar > 0) {
              setImportedCount(importedSoFar)
              setImportCompleted(true)
            }
            return
          }
        }

        setImportProgress(null)
        setImportedCount(users.length)
        clearWizardState(authUser.id, authCompany?.id)
        setImportCompleted(true)
      } else {
        const response = await Api.post('/core/event', body)
        showNotification(importData.newUsers, importData.importAllUsers, response.correlationId as string, authCompany.platform)
        // Show done screen
        setImportedCount(tableRows.length)
        clearWizardState(authUser.id, authCompany?.id)
        setImportCompleted(true)
      }
    } catch (error) {
      logger.error('error', error)
    }
  }

  const showErrorNotification = (error) => {
    const errorDescription = error.response?.data?.message ? error.response?.data.message : error.message ? error.message : JSON.stringify(error)
    notification.error({
      message: formatMessage({ id: 'error.generic' }),
      description: errorDescription,
      duration: 0,
    })
  }

  const showNotification = (users: IImportUser[], importAllUsers: boolean, correlationId: string, platform: string) => {
    if (importAllUsers) {
      notification.open({
        message: formatMessage({ id: 'users.import.description' }),
        duration: 15,
      })
      return
    }

    notification.open({
      key: correlationId,
      message: users.length === 1 ? formatMessage({ id: 'users.importOne.InProgress' }) : formatMessage({ id: 'users.importMany.InProgress' }),
      description: <Progress percent={0} size="small" />,
      duration: 0,
    })
    setCorrelationId(correlationId)
    setEventsNotifications({
      ...eventsNotifications,
      [correlationId]: {
        eventType: 'USER_STATUS_CHANGED',
        payload: {
          users: users.map((user) => `${platform}-${user.userId}`),
          totalUsers: users.length,
        },
      },
    })
  }

  const errorNotificationHandler = (error: Error, platform: Platform, onClickHandler: Function) => {
    const isTokenError = error.message === 'no_tokens'
    const errorMessageDescription = isTokenError ? formatMessage({ id: 'error.token.description' }, { platform }) : error.message
    notification.error({
      message: formatMessage({ id: 'error.generic' }),
      description: errorMessageDescription,
      key: 'error-notification-handler',
      btn: isTokenError && (
        <Button
          onClick={() => {
            onClickHandler()
            notification.destroy('error-notification-handler')
          }}
        >
          {formatMessage({ id: 'error.token.button' }, { platform })}
        </Button>
      ),
      duration: 0,
    })
    if (!isTokenError) {
      Sentry.captureException(JSON.stringify(error))
    }
  }

  const onSetImportAllUsers = (v) => {
    setSelectedUsers([])
    setImportAllUsers(v)
  }

  const convertNotImportedUsersToTableRows = useCallback(
    (users: IUserToImportData[]): IImportTableRow[] => {
      return users.map((user) => {
        const parsed = JSON.parse(user.value)
        return {
          id: parsed.id,
          name: parsed.realName || parsed.name,
          email: parsed.mail || '',
          imageUrl: parsed.imageUrl,
          departmentId: defaultDepartmentId,
          locationId: defaultLocationId,
          sendWelcomeMessage: true,
        }
      })
    },
    [defaultDepartmentId, defaultLocationId]
  )

  const fetchAllMicrosoftUsers = async (retryCount = 0) => {
    setIsFetchingAllUsers(true)
    try {
      const accessToken = (await msAuth.refreshTokens()).accessToken
      const teamId = getEffectiveMsTeamId(msTenantId)
      const msUsers = await msAuth.getUsers(accessToken, teamId)

      // Filter out already imported users (same logic as filterUsers)
      const filteredUsers = msUsers.filter((msUser) => {
        const existingUser = activeUsers.find((u) => u.id === vtUserId(msUser.id))
        return !existingUser || existingUser.status === 'DELETED'
      })

      // Convert to IUserToImportData format
      const notImported: IUserToImportData[] = filteredUsers.map((user) => {
        const userName = getName(user)
        const userEmail = getEmail(user)
        return {
          label: `${userName} (${userEmail})`,
          value: JSON.stringify({
            id: user.id,
            name: userName,
            mail: userEmail,
          }),
        }
      })

      setNotImportedUsers(notImported)
      setHasLoadedNotImportedUsers(true)
      setTableRows(convertNotImportedUsersToTableRows(notImported))
    } catch (err) {
      if (err?.data?.error?.code === 'Request_ResourceNotFound' && !msGroupNotFoundRef.current) {
        msGroupNotFoundRef.current = true
        return fetchAllMicrosoftUsers(0)
      }
      if (
        err?.data?.error?.code === 'InvalidAuthenticationToken' ||
        err?.includes?.('FAILED CODE OR TOKEN FETCH') ||
        err?.includes?.('invalid_grant') ||
        (err?.includes?.('invalid_request') && err?.includes?.('refresh_token'))
      ) {
        setShowMsLoginRequiredModal(true)
        setImportAllUsers(false)
      } else if (retryCount < 1) {
        // Transient MS Graph errors (e.g. 404 after token refresh) - retry once
        await new Promise((resolve) => setTimeout(resolve, 1500))
        return fetchAllMicrosoftUsers(retryCount + 1)
      } else {
        captureException(err)
        errorNotificationHandler(err, 'microsoft', () => fetchAllMicrosoftUsers(0))
        setImportAllUsers(false)
      }
    } finally {
      setIsFetchingAllUsers(false)
    }
  }

  const handleImportAllToggle = useCallback(
    (value: boolean) => {
      onSetImportAllUsers(value)
      if (value) {
        if (platform === 'microsoft') {
          fetchAllMicrosoftUsers()
        } else {
          setTableRows(convertNotImportedUsersToTableRows(notImportedUsers))
        }
      } else {
        setTableRows([])
        setSelectedRowKeys([])
        setIsFetchingAllUsers(false)
      }
      track('IMPORT_USERS_TOGGLE_IMPORT_ALL_USERS', {
        platform,
        selectedValue: value,
        totalNumberOfUsers,
      })
    },
    [notImportedUsers, convertNotImportedUsersToTableRows, platform, totalNumberOfUsers]
  )

  // Auto-populate table when notImportedUsers loads and importAllUsers is already true
  useEffect(() => {
    if (importAllUsers && notImportedUsers.length > 0) {
      setTableRows(convertNotImportedUsersToTableRows(notImportedUsers))
    }
  }, [notImportedUsers, importAllUsers, convertNotImportedUsersToTableRows])

  const handleUpdateTableRow = useCallback((id: string, field: keyof Pick<IImportTableRow, 'departmentId' | 'locationId' | 'sendWelcomeMessage'>, value: string | boolean) => {
    setTableRows((prev) => prev.map((row) => (row.id === id ? { ...row, [field]: value } : row)))
  }, [])

  const handleRemoveTableRow = useCallback((id: string) => {
    setTableRows((prev) => prev.filter((row) => row.id !== id))
    setSelectedRowKeys((prev) => prev.filter((key) => key !== id))
  }, [])

  const handleSearchSelectUser = useCallback(
    (userValue: string) => {
      const parsed = JSON.parse(userValue)
      // Don't add duplicates
      if (tableRows.some((row) => row.id === parsed.id)) {
        return
      }
      const newRow: IImportTableRow = {
        id: parsed.id,
        name: parsed.realName || parsed.name,
        email: parsed.mail || '',
        imageUrl: parsed.imageUrl,
        departmentId: defaultDepartmentId,
        locationId: defaultLocationId,
        sendWelcomeMessage: true,
      }
      setTableRows((prev) => [...prev, newRow])
    },
    [tableRows, defaultDepartmentId, defaultLocationId]
  )

  const handleBulkApplyDepartment = useCallback(
    (departmentId: string) => {
      setTableRows((prev) => prev.map((row) => (selectedRowKeys.includes(row.id) ? { ...row, departmentId } : row)))
    },
    [selectedRowKeys]
  )

  const handleBulkApplyLocation = useCallback(
    (locationId: string) => {
      setTableRows((prev) => prev.map((row) => (selectedRowKeys.includes(row.id) ? { ...row, locationId } : row)))
    },
    [selectedRowKeys]
  )

  const handleBulkApplyWelcomeMessage = useCallback(
    (value: boolean) => {
      setTableRows((prev) => prev.map((row) => (selectedRowKeys.includes(row.id) ? { ...row, sendWelcomeMessage: value } : row)))
    },
    [selectedRowKeys]
  )

  const handleRemoveSelected = useCallback(() => {
    setTableRows((prev) => prev.filter((row) => !selectedRowKeys.includes(row.id)))
    setSelectedRowKeys([])
  }, [selectedRowKeys])

  const csvImport = useCsvImport({
    notImportedUsers,
    existingRowIds: tableRows.map((r) => r.id),
    departments: departments.map((d) => ({ id: d.id, name: d.name })),
    locations: locations.map((l) => ({ id: l.id, name: l.name })),
    defaultDepartmentId,
    defaultLocationId,
  })

  const handleOpenCsvModal = useCallback(async () => {
    csvImport.openModal()
    // For Microsoft, directory users are not pre-fetched on page load (they use
    // on-demand search). Fetch them now so the rowHook can validate CSV emails
    // against the directory while the user uploads and maps columns.
    if (platform === 'microsoft' && !hasLoadedNotImportedUsers) {
      try {
        const accessToken = (await msAuth.refreshTokens()).accessToken
        const teamId = getEffectiveMsTeamId(msTenantId)
        const msUsers = await msAuth.getUsers(accessToken, teamId)

        const activeUserIds = new Set(activeUsers.filter((u) => u.status !== 'DELETED').map((u) => u.id))
        const filteredUsers = msUsers.filter((msUser) => !activeUserIds.has(vtUserId(msUser.id)))

        const notImported: IUserToImportData[] = filteredUsers.map((user) => {
          const userName = getName(user)
          const userEmail = getEmail(user)
          return {
            label: `${userName} (${userEmail})`,
            value: JSON.stringify({
              id: user.id,
              name: userName,
              mail: userEmail,
            }),
          }
        })

        setNotImportedUsers(notImported)
        setHasLoadedNotImportedUsers(true)
      } catch (err) {
        logger.warning('Failed to pre-fetch Microsoft users for CSV validation', err)
      }
    }
  }, [csvImport, platform, hasLoadedNotImportedUsers, activeUsers, msTenantId])

  const handleCsvSubmit = useCallback(
    (data: { validData: any[] }) => {
      const result = csvImport.processCsvData(data.validData)
      if (result.matched.length > 0) {
        setTableRows((prev) => [...prev, ...result.matched])
        message.success(formatMessage({ id: 'components.importUsersForm.notifications.csvUploadSuccess' }, { count: result.matched.length }))
      }
      csvImport.closeModal()
    },
    [csvImport, formatMessage]
  )

  const handleConfirmImport = useCallback(
    async (shouldProrate: boolean, prorateLeaveTypes: string[]) => {
      setShowConfirmModal(false)

      // Build config data - defaultLocation/defaultDepartment use the page-level defaults.
      // Per-user department/location/welcome overrides are sent via the users array.
      const configData = {
        shouldProrate,
        selectedLeaveTypes: prorateLeaveTypes,
        selectedLocation: defaultLocationId,
        selectedDepartment: defaultDepartmentId,
        sendWelcomeMessageTo: tableRows.length > 0 ? tableRows.some((r) => r.sendWelcomeMessage) : (authCompany?.announceNewUsers ?? true),
      }

      // Convert tableRows to the format expected by onFinish/import functions
      // Include per-user settings so they flow through to the event payload
      const usersForImport: IAutocompleteOptionValue[] = tableRows.map((row) => ({
        id: row.id,
        name: row.name,
        mail: row.email,
        imageUrl: row.imageUrl,
        departmentId: row.departmentId,
        locationId: row.locationId,
        sendWelcomeMessage: row.sendWelcomeMessage,
        birthday: row.birthday,
      }))
      setSelectedUsers(usersForImport)

      await onFinish(importAllUsers, usersForImport, configData)
    },
    [tableRows, importAllUsers, defaultLocationId, defaultDepartmentId, onFinish]
  )

  const submitBilling = async (seats) => {
    let response
    try {
      response = await Api.post('/core/event', {
        eventType: 'BILLING_UPDATED',
        eventGroup: 'BILLING',
        paymentProcessor: 'stripe',
        billingName: authCompany?.billing?.billingName,
        billingEmails: authCompany?.billing?.billingEmails,
        billingType: authCompany?.billing?.billingType,
        subscriptionPlan: authCompany?.billing?.nextSubscriptionPlan || authCompany?.billing?.subscriptionPlan,
        activeUsers: getJustActiveUsersNumber(activeUsers),
        // Only for the legacy plan, because we don't have info about period for legacy companies.
        subscriptionPeriod: authCompany?.billing?.nextSubscriptionPeriod || authCompany?.billing?.subscriptionPeriod,
        ...(isNumber(seats) && { seats }),
      })

      setCorrelationId(response.correlationId)
      notification.open({
        key: response.correlationId,
        message: formatMessage({ id: 'billing.updateInProgress' }),
        icon: <LoadingOutlined />,
        duration: 0,
      })
      setActionNotifications([...actionNotifications, response.correlationId])
    } catch (error) {
      handleBillingUpdateError(error, response?.correlationId)
    }
  }

  const handleBillingUpdateError = (error, correlationId?: string, title?: string): void => {
    const description = correlationId
      ? formatMessage({ id: 'notifications.error' }, { correlationId })
      : error.response?.data?.message
        ? error.response?.data?.message
        : error.message
          ? error.message
          : JSON.stringify(error)
    notification.error({
      message: title ? title : formatMessage({ id: 'billing.updatedFaild' }),
      description,
      duration: 0,
    })
  }

  const getJustActiveUsersNumber = (activeUsers: IGetUserListShort[]): number => {
    return activeUsers.filter((user) => user.status === 'ACTIVE').length
  }

  return (
    <div className="main-content">
      <PageHeader title={<IntlMessages id="users.importUsers" />} parent={[{ label: <IntlMessages id="app.users" />, to: '/app/users' }]} />
      <div className="main-content-body" data-testid="import-users-page-body">
        {importCompleted ? (
          <Result
            status="success"
            title={formatMessage({ id: 'components.importUsersForm.importSuccess.title' }, { count: importedCount })}
            subTitle={formatMessage({ id: 'components.importUsersForm.importSuccess.subtitle' })}
            extra={[
              <Button type="primary" key="users" onClick={() => history.push('/app/users')}>
                {formatMessage({ id: 'components.importUsersForm.importSuccess.goToUsers' })}
              </Button>,
            ]}
          />
        ) : importProgress ? (
          <ImportProgress
            completedBatches={importProgress.completedBatches}
            totalBatches={importProgress.totalBatches}
            importedUsers={importProgress.importedUsers}
            totalUsers={importProgress.totalUsers}
          />
        ) : (
          <>
            {/* Auto-sync notice (persistent, non-dismissable) when already enabled */}
            {authCompany?.importUsersAutomatically && !importAllUsers && <AutoSyncNotice platform={platform} />}

            <ImportAllToggle
              platform={platform}
              importAllUsers={importAllUsers}
              shouldEnableImportAll={shouldEnableImportAll}
              totalDirectoryUsers={totalNumberOfUsers}
              notImportedCount={notImportedUsers.length}
              importUsersAutomatically={importUsersAutomatically}
              onToggle={handleImportAllToggle}
              onAutoSyncChange={(value) => {
                if (platform === 'microsoft' && value) {
                  checkAutoImportAutomatically(value)
                }
                if (platform === 'google' && value) {
                  checkAutoImportAutomaticallyGoogle(value)
                }
                setImportUsersAutomatically(value)
              }}
            />
            {!importAllUsers && (
              <div>
                <UserSearch
                  platform={platform}
                  notImportedUsers={notImportedUsers}
                  tableRowIds={tableRows.map((r) => r.id)}
                  onSelectUser={handleSearchSelectUser}
                  onSearchUsers={onSearch}
                  onOpenCsvUpload={handleOpenCsvModal}
                />
              </div>
            )}

            {hasLoadedNotImportedUsers && notImportedUsers.length === 0 && totalNumberOfUsers > 0 ? (
              <Result
                status="success"
                title={formatMessage({ id: 'components.importUsersForm.allUsersAlreadyImported' })}
                extra={[
                  <Button key="settings" onClick={() => history.push('/app/settings/general')}>
                    {formatMessage({ id: 'components.importUsersForm.autoSyncManageSettings' })}
                  </Button>,
                  <Button type="primary" key="users" onClick={() => history.push('/app/users')}>
                    {formatMessage({ id: 'components.importUsersForm.importSuccess.goToUsers' })}
                  </Button>,
                ]}
              />
            ) : tableRows.length > 0 ? (
              <>
                <div style={{ border: '1px solid #f0f0f0', borderRadius: 8, overflow: 'hidden', marginTop: 16 }}>
                  <BulkActionsToolbar
                    selectedCount={selectedRowKeys.length}
                    departments={departments.map((d) => ({ id: d.id, name: d.name, parentId: d.parentId }))}
                    locations={locations.map((l) => ({ id: l.id, name: l.name }))}
                    onApplyDepartment={handleBulkApplyDepartment}
                    onApplyLocation={handleBulkApplyLocation}
                    onApplyWelcomeMessage={handleBulkApplyWelcomeMessage}
                    onRemoveSelected={handleRemoveSelected}
                  />

                  <ImportUsersTable
                    rows={tableRows}
                    departments={departments.map((d) => ({ id: d.id, name: d.name, parentId: d.parentId }))}
                    locations={locations.map((l) => ({ id: l.id, name: l.name }))}
                    selectedRowKeys={selectedRowKeys}
                    onSelectChange={setSelectedRowKeys}
                    onUpdateRow={handleUpdateTableRow}
                    onRemoveRow={handleRemoveTableRow}
                  />
                </div>
                <Text type="secondary" style={{ display: 'block', marginTop: 8, fontSize: 12 }}>
                  <IntlMessages id="components.importUsersForm.belowTableNote" />
                </Text>
              </>
            ) : isFetchingAllUsers ? (
              <div style={{ textAlign: 'center', padding: '48px 0' }}>
                <Spin size="large" />
                <div style={{ marginTop: 16 }}>
                  <Text type="secondary">{formatMessage({ id: 'components.importUsersForm.loadingAllUsers' })}</Text>
                </div>
              </div>
            ) : (
              <div className="import-page-empty-state">
                <PiUsers size={48} className="import-page-empty-state-icon" />
                <Text strong style={{ fontSize: 16 }}>
                  <IntlMessages id="components.importUsersForm.emptyStateTitle" />
                </Text>
                <Text type="secondary" style={{ marginTop: 4 }}>
                  <IntlMessages id="components.importUsersForm.emptyStateSubtitle" />
                </Text>
              </div>
            )}

            <ImportFooter
              userCount={tableRows.length}
              importAllUsers={importAllUsers}
              disabled={hasLoadedNotImportedUsers && notImportedUsers.length === 0 && totalNumberOfUsers > 0}
              onCancel={() => {
                /* navigation handled by Link */
              }}
              onReviewImport={() => setShowConfirmModal(true)}
              platform={platform}
              upcomingInvoiceInfo={upcomingInvoiceInfo}
              isTrial={authCompany?.subscriptionStatus === 'trialing' && !authCompany?.billing?.paymentMethodAdded}
              isFreemium={authCompany?.plan === SubscriptionPlanEnum.freemium}
            />
          </>
        )}
      </div>
      <ConfirmModal
        open={showConfirmModal}
        tableRows={tableRows}
        importAllUsers={importAllUsers}
        locations={locations}
        leaveTypes={leaveTypes}
        defaultLocationId={defaultLocationId}
        companyShouldProrate={companyShouldProrate}
        companyProratedLeaveTypes={companyProratedLeaveTypes}
        upcomingInvoiceInfo={upcomingInvoiceInfo}
        isLoadingUpcomingInvoiceInfo={isLoadingUpcomingInvoiceInfo}
        isAnnualBilling={authCompany?.billing?.subscriptionPeriod === 'annual'}
        isTrial={authCompany?.subscriptionStatus === 'trialing' && !authCompany?.billing?.paymentMethodAdded}
        isFreemium={authCompany?.plan === SubscriptionPlanEnum.freemium}
        locale={authUser?.locale || LocaleEnum.en}
        onConfirm={handleConfirmImport}
        onCancel={() => setShowConfirmModal(false)}
      />
      {showMsLoginRequiredModal && (
        <Modal
          title={formatMessage({ id: 'error.microsoft.notificationsLoginRequiredTitle' })}
          open={showMsLoginRequiredModal}
          onOk={getBasicMsToken}
          onCancel={() => {
            setShowMsLoginRequiredModal(false)
            history.push('/app/users')
          }}
        >
          <p>
            <IntlMessages id="microsoft.usersLoginRequiredDescription1" />
          </p>
          <p>
            <IntlMessages id="microsoft.usersLoginRequiredDescription2" />
          </p>
        </Modal>
      )}
      {showMsLoginAdditionalScopesRequiredModal && (
        <Modal
          title={formatMessage({ id: 'microsoft.additionalPermissionsRequiredTitle' })}
          open={showMsLoginAdditionalScopesRequiredModal}
          onOk={() => {
            getWebAppTokens(msTenantId)
          }}
          onCancel={() => {
            setImportUsersAutomatically(false)
            setShowMsLoginAdditionalScopesRequiredModal(false)
          }}
        >
          <p>
            <IntlMessages id="microsoft.additionalPermissionsRequiredDescription" />
          </p>
        </Modal>
      )}
      {showSlackLoginRequiredModal && (
        <Modal
          title={formatMessage({ id: 'error.slack.usersLoginRequiredTitle' })}
          open={showSlackLoginRequiredModal}
          onOk={getSlackTokens}
          onCancel={() => {
            setShowSlackLoginRequiredModal(false)
            history.push('/app/users')
          }}
        >
          <p>
            <IntlMessages id="slack.notificationsLoginRequiredDescription1" />
          </p>
          <p>
            <IntlMessages id="slack.notificationsLoginRequiredDescription2" />
          </p>
        </Modal>
      )}
      {showGoogleLoginRequiredModal && (
        <Modal
          title={formatMessage({ id: 'error.google.loginRequiredTitle' })}
          open={showGoogleLoginRequiredModal}
          onOk={() => getGoogleTokens(googleScopes?.googleApiScopes?.includes('https://www.googleapis.com/auth/admin.directory.user.readonly'))}
          onCancel={() => {
            setShowGoogleLoginRequiredModal(false)
            history.push('/app/users')
          }}
          okButtonProps={{ disabled: isValidatingGooglePermissions }}
        >
          <p>
            <IntlMessages id="error.google.usersLoginRequiredDescription1" />
          </p>
          <p>
            <IntlMessages id="error.google.usersLoginRequiredDescription2" />
          </p>
        </Modal>
      )}
      {showEnableGoogleApiModal && (
        <Modal
          title={formatMessage({ id: 'error.google.directoryApiDisabledTitle' })}
          open={showEnableGoogleApiModal}
          footer={null}
          onCancel={() => {
            setShowEnableGoogleApiModal(false)
          }}
        >
          <Paragraph>
            <IntlMessages id="error.google.directoryApiDisabledP1" />
          </Paragraph>
          <Paragraph>
            <IntlMessages id="error.google.directoryApiDisabledP2" />
          </Paragraph>
          <Paragraph copyable={{ text: supportLink }}>
            <a href={supportLink} target="_blank" rel="noopener noreferrer">
              {supportLink}
            </a>
          </Paragraph>
          <Paragraph>
            <IntlMessages id="error.google.directoryApiDisabledP4" />
          </Paragraph>
        </Modal>
      )}
      {showRequestGoogleAdminPermissionsModal && (
        <Modal
          title={formatMessage({ id: 'connect.google.sharedPermissionsRequired' })}
          open={showRequestGoogleAdminPermissionsModal}
          footer={null}
          onCancel={() => {
            setShowRequestGoogleAdminPermissionsModal(false)
          }}
        >
          <Paragraph>
            <IntlMessages id="connect.google.sharedPermissionsRequiredDescription" />
          </Paragraph>
          <Paragraph copyable={{ text: getGoogleAdminExtraPermissionsLink() }}>
            <a target="_blank" rel="noopener noreferrer">
              <IntlMessages id="app.link" />
            </a>
          </Paragraph>
        </Modal>
      )}
      {showGoogleInsufficientPermissionsModal && (
        <Modal
          title={formatMessage({ id: 'error.google.insufficientAdminPermissionsTitle' })}
          open={showGoogleInsufficientPermissionsModal}
          footer={null}
          onCancel={() => {
            setShowGoogleInsufficientPermissionsModal(false)
          }}
        >
          <Paragraph>
            <IntlMessages id="error.google.insufficientAdminPermissionsDescription" />
          </Paragraph>
          <Paragraph>
            <IntlMessages id="error.google.superAdminRequired" />
          </Paragraph>
          {adminTokenHash && (
            <>
              <Paragraph>
                <IntlMessages id="connect.google.sharedPermissionsRequiredDescription" />
              </Paragraph>
              <Paragraph copyable={{ text: getGoogleAdminExtraPermissionsLink() }}>
                <a href={getGoogleAdminExtraPermissionsLink()} target="_blank" rel="noopener noreferrer">
                  {getGoogleAdminExtraPermissionsLink()}
                </a>
              </Paragraph>
            </>
          )}
        </Modal>
      )}
      <Modal
        title={formatMessage({ id: 'microsoft.notificationsLoginRequiredTitle' })}
        open={Boolean(authCompany?.platform === 'microsoft' && isMsAdminConsentRequired && importUsersAutomatically && adminConsentUrl)}
        onCancel={() => {
          setMsAdminConsentRequired(false)
        }}
        footer={null}
      >
        <Paragraph>
          <IntlMessages id="components.importUsersForm.consent.line1" />
        </Paragraph>
        <Paragraph copyable={{ text: adminConsentUrl as string }}>
          <a href={adminConsentUrl as string}>{adminConsentUrl}</a>
        </Paragraph>
      </Modal>
      <ReactSpreadsheetImport
        isOpen={csvImport.isOpen}
        onClose={csvImport.closeModal}
        onSubmit={handleCsvSubmit}
        rowHook={validateImportUsersRow({
          notImportedUsersRef,
          activeUsersRef,
          departmentsRef,
          locationsRef,
          hasLoadedNotImportedUsersRef,
          formatMessage,
        })}
        fields={[
          {
            label: formatMessage({ id: 'app.email' }),
            key: 'email',
            alternateMatches: ['email', 'employee email', 'user email', 'e-mail'],
            fieldType: { type: 'input' },
            example: 'user@company.com',
            validations: [{ rule: 'required', errorMessage: formatMessage({ id: 'components.importUsersForm.csvFieldEmailRequired' }), level: 'error' }],
          },
          {
            label: formatMessage({ id: 'app.department' }),
            key: 'department',
            alternateMatches: ['department', 'team', 'group'],
            fieldType: { type: 'input' },
            example: 'Engineering',
          },
          {
            label: formatMessage({ id: 'app.location' }),
            key: 'location',
            alternateMatches: ['location', 'office', 'site'],
            fieldType: { type: 'input' },
            example: 'New York',
          },
          {
            label: formatMessage({ id: 'components.importUsersForm.csvFieldNotify' }),
            key: 'notify',
            alternateMatches: ['notify new users', 'notify', 'welcome', 'send welcome', 'welcome message'],
            fieldType: { type: 'input' },
            example: 'TRUE',
          },
          {
            label: formatMessage({ id: 'user.birthday' }),
            key: 'birthday',
            alternateMatches: ['date of birth', 'birth date', 'DOB', 'birthday'],
            fieldType: { type: 'input' },
            example: '1990-05-15',
          },
        ]}
      />
    </div>
  )
}

export default ImportUsersPage
